var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable import/no-cycle */
import Injector, { InjectorScope } from '@slickdeals/durango/dist/javascript/core/injector';
import { inject } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { name as controllerName, CSS_INDICATOR_CLASSNAME, CSS_CONTENTS_CLASSNAME, } from '../utilities/autoPlayableController';
import BlueprintComponent from '../core/blueprintComponent';
/** The name of the module */
export const name = 'AutoPlayable';
/** The dependencies that we need for the autoplayable decorator */
const dependencies = {
    ...Injector.createDependency(controllerName, InjectorScope.Singleton),
};
/** Class implementing the pieces of the decorator so we can leverage the injector */
class AutoPlayableDecorator {
    /** Constructor for the wrapping class that assigns the properties */
    constructor(element, options, transitionCallback, intervalQueueKey) {
        this.element = element;
        this.options = options;
        this.transitionCallback = transitionCallback;
        this.intervalQueueKey = intervalQueueKey;
    }
    registerAutoplay(transitionCallback, controller) {
        const elements = this.buildElementsMap();
        const mergedOptions = {
            ...this.options,
            transitionCallback,
        };
        const context = controller.start({
            elements,
            options: mergedOptions,
            container: this.element,
        });
        if (context.isCycling()) {
            this.intervalQueueKey = context.getQueueKey();
        }
    }
    startAutoplay(controller) {
        if (this.intervalQueueKey) {
            controller.restart(this.intervalQueueKey);
        }
    }
    stopAutoplay(controller) {
        if (this.intervalQueueKey) {
            controller.stop(this.intervalQueueKey);
        }
    }
    buildElementsMap() {
        const autoPlayableElements = new Map();
        const indicators = this.element.querySelectorAll(`.${CSS_INDICATOR_CLASSNAME}`);
        const contents = this.element.querySelectorAll(`.${CSS_CONTENTS_CLASSNAME}`);
        /**
         * We are using a for loop here instead of a forEach on
         * the nodes due to the connotations of each. a forEach
         * implies that we are only manipulating the iterated
         * object.
         *
         * With this, we are having to manipulate two sets of
         * nodes so we use a traditional for.
         */
        for (let i = 0; i < contents.length; i += 1) {
            /**
             * If no indicator, use the index + 1 so we don't have to
             * worry about 0 being construed as undefined.
             */
            autoPlayableElements.set(indicators[i] || i + 1, contents[i]);
        }
        return autoPlayableElements;
    }
}
__decorate([
    inject(dependencies)
], AutoPlayableDecorator.prototype, "registerAutoplay", null);
__decorate([
    inject(dependencies)
], AutoPlayableDecorator.prototype, "startAutoplay", null);
__decorate([
    inject(dependencies)
], AutoPlayableDecorator.prototype, "stopAutoplay", null);
/**
 * The decorator implementation for the component.
 */
export function autoplayable({ transitionCallback, } = {}) {
    return (target) => {
        const { prototype: proto } = target;
        // proto.transitionCallback = this.transitionCallback || transitionCallback
        /** Using a callback here so we can proxy things out to the decorator wrapping class */
        proto.stopAutoplay = function stopAutoplay() {
            this.autoPlayableDecorator.stopAutoplay();
        };
        /** Using a callback here so we can proxy things out to the decorator wrapping class */
        proto.startAutoplay = function startAutoplay() {
            this.autoPlayableDecorator.startAutoplay();
        };
        /** Bind the registration function to the component instance using the decorator */
        proto.registerAutoplay = function registerAutoplay() {
            /** New the instance of the wrapping class per component that uses the decorator */
            const decorator = new AutoPlayableDecorator(this.element, this.options.autoplayOptions, this.intervalQueueKey);
            this.autoPlayableDecorator = decorator;
            this.autoPlayableDecorator.registerAutoplay(this.transitionCallback || transitionCallback);
        };
        BlueprintComponent.addToInitQueue(target.name, proto.registerAutoplay);
    };
}
